import React, { useEffect, useState } from 'react';
import { Hub } from 'aws-amplify';
import { Link, useLocation } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';

import HeaderBar from './HeaderBar';
import IconRail from './IconRail';
import { getNavItems, getActiveNavItem } from './NavigationListItems';
import { SubNavHostContext } from './subNavHost';

import useLogout from '../../hooks/useLogout';
import { fetchWhiteListedFileTypes } from '../../app/store/fileSlice';
import StandardDialog, { StandardDialogActions } from '../../features/Modals/StandardDialog';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setShowTokenExpiredModal } from '../../app/store/userSlice';
import getAuthSession, { MAX_SESSION_LENGTH } from '../../services/auth';

const TPNShield = `${process.env.PUBLIC_URL}/assets/TPN-Logo-Shield.png`;

const Shell = styled('div')({ display: 'flex', flexDirection: 'column', height: '100vh' });
const Body = styled('div')({ display: 'flex', flex: 1, minHeight: 0 });
// Brand block + icon rail share the 90px column so a hoisted sub-nav can run the full viewport height beside it.
const LeftCol = styled('div')({ width: 90, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#142F53', '& > nav': { flex: 1 } });
const BrandBlock = styled(Link)({ height: 56, flexShrink: 0, background: '#142F53', display: 'flex', alignItems: 'center', justifyContent: 'center' });
const ShieldImg = styled('img')({ height: 36 });
// overflow clips the header bar's drop shadow at the column edge so it can't double the sub-nav's right border.
const MainCol = styled('div')({ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' });
// Pages portal a full-height secondary nav here (company profile); empty it takes no width.
const SubNavHost = styled('div')({ display: 'flex', minHeight: 0 });



/**
 * The site-wide navigation shell: a fixed top header bar and a fixed 90px icon
 * rail wrapping the page content. Also hosts the global chrome that used to live
 * in the drawer — the token-expiry modal, its `Hub` listener, and the
 * session-expiry trigger.
 */
export function SideNav({ children, banner }: { children: React.ReactNode; banner?: React.ReactNode }) {
  const { user } = useAppSelector((state) => state.user);
  const showTokenExpiredModal = useAppSelector(
    (state) => state.user?.showTokenExpiredModal ?? false,
  );

  const dispatch = useAppDispatch();
  const handleLogout = useLogout();
  const location = useLocation();

  const [expirationTime, setExpirationTime] = useState<number | null>(null);

  const items = getNavItems(user);
  const activeItem = getActiveNavItem(items, location.pathname);

  const logOutUser = () => {
    handleLogout();
    dispatch(setShowTokenExpiredModal(false));
  };

  // Whitelisted file types + token-refresh-failure modal (kept from the drawer).
  useEffect(() => {
    dispatch(fetchWhiteListedFileTypes());

    const unsubscribe = Hub.listen('auth', ({ payload: { event } }) => {
      if (event !== 'tokenRefresh_failure') return;
      dispatch(setShowTokenExpiredModal(true));
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Session-expiry tracking. The live countdown is no longer displayed, but the
  // 24h max-session logout and the expiry modal trigger are preserved.
  const updateExpirationTime = async () => {
    try {
      const authSession = await getAuthSession();
      const accessToken = authSession.getAccessToken();
      const newExpiration = accessToken.getExpiration() * 1000;

      const loginTimeStr = localStorage.getItem('fixedLoginTime');
      if (!loginTimeStr) {
        const payload = accessToken.decodePayload?.();

        if (!payload?.iat) {
          console.warn('Access token missing "iat" field. Forcing logout.');
          await handleLogout();
          return;
        } else {
          const issuedAt = payload.iat * 1000;
          localStorage.setItem('fixedLoginTime', issuedAt.toString());
        }
      }

      const finalLoginTimeStr = localStorage.getItem('fixedLoginTime');
      const loginTime = finalLoginTimeStr ? parseInt(finalLoginTimeStr, 10) : Date.now();
      const sessionExpiresAt = loginTime + MAX_SESSION_LENGTH;

      if (Date.now() > sessionExpiresAt) {
        console.warn('Session exceeded 24 hours — logging out.');
        await handleLogout();
        return;
      }

      if (newExpiration !== expirationTime && !showTokenExpiredModal) {
        setExpirationTime(newExpiration);
      }
    } catch (error) {
      console.error('Error fetching session:', error);
    }
  };

  useEffect(() => {
    updateExpirationTime();
    const refreshInterval = setInterval(updateExpirationTime, 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    if (!expirationTime) return;

    const remaining = expirationTime - Date.now();
    if (remaining <= 0) {
      dispatch(setShowTokenExpiredModal(true));
      return;
    }

    const timeoutId = setTimeout(() => {
      dispatch(setShowTokenExpiredModal(true));
    }, remaining);

    return () => clearTimeout(timeoutId);
  }, [expirationTime, dispatch]);

  const [subNavHost, setSubNavHost] = useState<HTMLElement | null>(null);

  return (
    <SubNavHostContext.Provider value={subNavHost}>
    <Shell>
      {/* Global strips (masquerade) mount above the body, outside every scroll container, so they can never scroll away. */}
      {banner}
      <Body>
        <LeftCol>
          <BrandBlock to="/" aria-label="TPN+ home">
            <ShieldImg src={TPNShield} alt="TPN+ home" />
          </BrandBlock>
          <IconRail items={items} activeId={activeItem?.id} />
        </LeftCol>
        <SubNavHost ref={setSubNavHost} />
        <MainCol>
          <HeaderBar />
          {children}
        </MainCol>
      </Body>
      <StandardDialog cantClose={true} title="Your Login Has Expired" handleClose={() => { return; }} isOpen={showTokenExpiredModal}>
        <Typography>Your login has expired. Please click the button below to login again.</Typography>
        <StandardDialogActions>
          <Button variant="contained" onClick={() => logOutUser()}>Take me to the Login Form</Button>
        </StandardDialogActions>
      </StandardDialog>
    </Shell>
    </SubNavHostContext.Provider>
  );
}
